import { z } from "zod";
import { CURRENCY_CODES } from "../money/currency.ts";
import { FEATURE_CODES } from "../catalog/features.ts";
import { PERMISSION_KEYS } from "../catalog/permissions.ts";
import { PSI_PERMISSION_KEYS } from "../catalog/psi-permissions.ts";
import { QuotaStateSchema } from "../catalog/quotas.ts";
import { STORE_CAPABILITIES } from "../catalog/capabilities.ts";
import { LOCALE_CODES } from "../i18n/locales.ts";
import { StatisticsBasisSchema } from "./store.ts";

/**
 * `GET /me` — user, tenant, role, permissions, features, quotas and nav in one
 * call. The source needs three round trips before its shell can render
 * (`04-api-surface.md`); this collapses them.
 */

// A self-referencing schema must be a named component or the OpenAPI generator
// recurses without end trying to inline it.
export const NavItemSchema = z
  .object({
    key: z.string(),
    path: z.string(),
    labelKey: z.string(),
    icon: z.string().optional(),
    get children() {
      return z.array(NavItemSchema).optional();
    },
  })
  .meta({ id: "NavItem" });

export type NavItem = {
  key: string;
  path: string;
  labelKey: string;
  icon?: string;
  children?: NavItem[];
};

export const BrandSchema = z.object({
  name: z.string(),
  logoUrl: z.string().nullable(),
  faviconUrl: z.string().nullable(),
  fontFamily: z.string().nullable(),
  /** Maps onto the `--brand-*` custom properties in `@tovi/ui`'s theme.css. */
  colorTokens: z.record(z.string(), z.string()),
});

export type Brand = z.infer<typeof BrandSchema>;

export const MeStoreSchema = z.object({
  id: z.uuid(),
  chainId: z.uuid(),
  name: z.string(),
  slug: z.string(),
  isMain: z.boolean(),
  timezone: z.string(),
  businessDayCutoff: z.string(),
  /** Which of the two dates this store's reports read. Not the cutoff above. */
  statisticsBasis: StatisticsBasisSchema,
  /**
   * What trading day it is in this store right now, resolved by the server.
   * The client cannot work it out: it needs the store's timezone *and* its
   * cutoff, and a browser in another timezone would default every report to
   * the wrong day.
   */
  businessDate: z.string(),
  primaryCurrency: z.enum(CURRENCY_CODES),
  secondCurrency: z.enum(CURRENCY_CODES).nullable(),
  /** Scaled by `EXCHANGE_RATE_SCALE`; serialised as a string because it is a bigint. */
  exchangeRate: z.string(),
  languages: z.array(z.enum(LOCALE_CODES)),
  marketProfileCode: z.string(),
});

export type MeStore = z.infer<typeof MeStoreSchema>;

export const MeUserSchema = z.object({
  id: z.uuid(),
  username: z.string(),
  displayName: z.string(),
  email: z.email().nullable(),
  realm: z.enum(["merchant", "operator"]),
  roleCode: z.string().nullable(),
  roleName: z.string().nullable(),
});

export type MeUser = z.infer<typeof MeUserSchema>;

/** One row of the store switcher (`12-chain.md` § 3). */
export const MeReachableStoreSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  isMain: z.boolean(),
});

export type MeReachableStore = z.infer<typeof MeReachableStoreSchema>;

export const MeResponseSchema = z.object({
  user: MeUserSchema,
  store: MeStoreSchema.nullable(),
  /**
   * Every store this account may switch into, name-ordered
   * (`12-chain.md` §§ 1, 3). It is identity, not a chain rollup, so it is not
   * behind `CHAIN_STORE` — and a single-site merchant simply gets one row,
   * which is what tells the shell not to draw a switcher at all.
   */
  reachableStores: z.array(MeReachableStoreSchema),
  permissions: z.array(z.enum(PERMISSION_KEYS)),
  /**
   * Inventory rights, in their own array (`11-inventory.md` § 2).
   *
   * Two arrays and not one, for the same reason the server keeps two columns
   * and two middlewares: neither set implies the other, and a `psi:*` key
   * folded in beside the POS keys would be checked by the POS gate the first
   * time somebody reached for it.
   *
   * The engine already filtered the merchant nav on these, so a stock
   * controller reached the inventory screens and then met a console that could
   * not tell whether they were allowed to write on them.
   */
  psiPermissions: z.array(z.enum(PSI_PERMISSION_KEYS)),
  features: z.array(z.enum(FEATURE_CODES)),
  quotas: z.record(z.string(), QuotaStateSchema),
  /**
   * What head office has delegated to this store (`12-chain.md` § 2). Present
   * for every store, chain or not — a single-site merchant holds the catalog
   * defaults, so the UI has one rule rather than a chain branch.
   */
  capabilities: z.record(z.enum(STORE_CAPABILITIES), z.boolean()),
  nav: z.array(NavItemSchema),
  brand: BrandSchema,
  locale: z.enum(LOCALE_CODES),
});

export type MeResponse = z.infer<typeof MeResponseSchema>;

export const SwitchStoreRequestSchema = z.object({ storeId: z.uuid() });
export type SwitchStoreRequest = z.infer<typeof SwitchStoreRequestSchema>;
